/**
 * MarketDataSynthesis Component
 *
 * Unified narrative that synthesizes Zandi macro intelligence with local micro data
 * to tell a cohesive investment story. Explains what the COMBINATION of data sources
 * reveals about a specific property opportunity.
 */

import { Brain, Shield, Zap } from 'lucide-react';
import type { PropertyAnalysisResult, SubmarketScore, UpliftPosition, ZandiAdjustedProjection } from '../../types/calculations';
import type { FullComparisonResponse } from '../../types/yardi';
import { SectionTitle } from '../Luxury';
import { useActiveMarket } from '../../store/useMarketStore';
// formatCurrency, formatPercent unused — synthesis uses inline string formatting

interface MarketDataSynthesisProps {
  data: PropertyAnalysisResult;
  yardiData?: FullComparisonResponse | null;
}

// Score to strength label
const strengthLabel = (score: number): string => {
  if (score >= 80) return 'very strong';
  if (score >= 65) return 'strong';
  if (score >= 50) return 'moderate';
  if (score >= 35) return 'weak';
  return 'very weak';
};

// Score to color class
const scoreColor = (score: number): string => {
  if (score >= 75) return 'text-emerald';
  if (score >= 55) return 'text-amber-400';
  return 'text-red-400';
};

// TrendIcon removed — TrendingUp/TrendingDown/Minus imports removed with it

/**
 * Build the synthesis narrative from combined data sources.
 */
function buildSynthesis(
  zandi: SubmarketScore,
  _uplift: UpliftPosition | undefined,
  zandiProj: ZandiAdjustedProjection | undefined,
  data: PropertyAnalysisResult,
  yardiData: FullComparisonResponse | null | undefined,
  marketDisplayName: string,
): string[] {
  const paragraphs: string[] = [];
  const neighborhood = data.inputs?.neighborhood || data.inputs?.submarket || zandi.name;
  const compositeScore = zandi.composite_score;
  const phase = zandiProj?.submarket_phase || 'unknown';

  // --- Paragraph 1: Market Position Overview ---
  const rentGap = zandi.rent_gap_score;
  const momentum = zandi.momentum_score;
  const vacancy = zandi.vacancy_score;
  const cagr = zandi.raw_data.cagr_5yr ?? zandi.raw_data.egi_cagr_5yr;
  const cagrStr = cagr != null ? `${(cagr > 1 ? cagr : cagr * 100).toFixed(1)}%` : null;

  let openingLine = `${neighborhood} scores ${compositeScore.toFixed(0)}/100 on our composite index, placing it in ${strengthLabel(compositeScore)} territory`;
  if (zandi.rank && zandi.neighborhood_count) {
    openingLine += ` (#${zandi.rank} of ${zandi.neighborhood_count} ${marketDisplayName} submarkets)`;
  }
  openingLine += '.';

  let contextLine = '';
  if (phase !== 'unknown') {
    contextLine = ` The submarket is in an "${phase}" cycle phase`;
    if (cagrStr) {
      contextLine += ` with a ${cagrStr} 5-year EGI compound annual growth rate`;
    }
    contextLine += '.';
  }

  paragraphs.push(openingLine + contextLine);

  // --- Paragraph 2: Rent Intelligence (Zandi + Local) ---
  const zandiEgi = zandi.raw_data.egi_per_unit;
  const currentRent = data.rent_capex_transparency?.current_rent_per_unit || data.inputs?.rent_per_unit;

  if (zandiEgi != null && currentRent != null) {
    const currentToZandi = ((zandiEgi - currentRent) / currentRent * 100);
    let rentNarrative = `Zandi's EGI benchmark of $${zandiEgi.toLocaleString()}/unit against the property's current rent of $${currentRent.toLocaleString()}/unit reveals a ${currentToZandi.toFixed(0)}% rent gap.`;

    if (currentToZandi > 20) {
      rentNarrative += ` This substantial gap signals significant unrealized rent potential — the property is meaningfully underpriced relative to what the submarket supports.`;
    } else if (currentToZandi > 5) {
      rentNarrative += ` This moderate gap indicates room for rent growth through unit improvements and lease-up at market rates.`;
    } else if (currentToZandi > -5) {
      rentNarrative += ` Current rents are roughly at market — upside will come from organic growth rather than repositioning.`;
    } else {
      rentNarrative += ` Current rents exceed the Zandi benchmark — validate that premium rents are sustainable for this micro-location.`;
    }

    // Cross-reference with Yardi
    if (yardiData?.rent_growth_comparison) {
      const yardiGrowth = yardiData.rent_growth_comparison.yardi_value * 100;
      const zandiGrowthPct = cagr != null ? (cagr > 1 ? cagr : cagr * 100) : null;
      if (zandiGrowthPct != null) {
        const divergence = Math.abs(zandiGrowthPct - yardiGrowth);
        if (divergence < 1.5) {
          rentNarrative += ` Yardi's metro-level ${yardiGrowth.toFixed(1)}% rent growth aligns with Zandi's submarket data, reinforcing the growth thesis.`;
        } else if (zandiGrowthPct > yardiGrowth) {
          rentNarrative += ` Notably, this submarket's ${zandiGrowthPct.toFixed(1)}% growth outpaces Yardi's ${yardiGrowth.toFixed(1)}% metro average — ${neighborhood} is pulling ahead of the broader ${marketDisplayName} market.`;
        } else {
          rentNarrative += ` However, this submarket's ${zandiGrowthPct.toFixed(1)}% growth lags Yardi's ${yardiGrowth.toFixed(1)}% metro average — the broader market is moving faster.`;
        }
      }
    }

    paragraphs.push(rentNarrative);
  }

  // --- Paragraph 3: Supply-Demand Dynamics ---
  const vacancyRate = zandi.raw_data.vacancy_rate;
  const permitScore = zandi.permit_score;
  const mobi = zandi.mobi_score;

  let supplyDemand = '';
  if (vacancyRate != null) {
    const vacancyPct = vacancyRate > 1 ? vacancyRate : vacancyRate * 100;
    supplyDemand = `Local vacancy sits at ${vacancyPct.toFixed(1)}%`;
    if (vacancyPct < 3) {
      supplyDemand += ` — extremely tight, giving landlords strong pricing power.`;
    } else if (vacancyPct < 5) {
      supplyDemand += ` — below the national 5.5% average, indicating healthy tenant demand.`;
    } else if (vacancyPct < 8) {
      supplyDemand += ` — near equilibrium, with balanced supply and demand.`;
    } else {
      supplyDemand += ` — elevated, suggesting tenant options are plentiful and landlord pricing power is limited.`;
    }
  }

  if (permitScore != null) {
    if (permitScore < 30) {
      supplyDemand += ` New development activity is minimal (permit score: ${permitScore.toFixed(0)}/100), meaning little new supply will dilute demand.`;
    } else if (permitScore < 60) {
      supplyDemand += ` Moderate development activity (permit score: ${permitScore.toFixed(0)}/100) — some new supply is entering but not at a pace that threatens absorption.`;
    } else {
      supplyDemand += ` High development activity (permit score: ${permitScore.toFixed(0)}/100) — significant new supply is coming, which could pressure rents if absorption slows.`;
    }
  }

  if (mobi != null && mobi > 0) {
    if (mobi >= 70) {
      supplyDemand += ` The affordability index (${mobi.toFixed(0)}/100) shows rents remain accessible relative to local incomes, supporting tenant retention and lease renewals.`;
    } else if (mobi >= 40) {
      supplyDemand += ` Affordability is moderate (${mobi.toFixed(0)}/100) — rents are approaching the ceiling of local household budgets.`;
    }
  }

  if (supplyDemand) paragraphs.push(supplyDemand);

  // --- Paragraph 4: Growth Outlook (Zandi-Adjusted Projections) ---
  if (zandiProj) {
    const baselineCF = zandiProj.baseline_total_cash_flow_10yr;
    const adjustedCF = zandiProj.adjusted_total_cash_flow_10yr;
    const deltaCF = zandiProj.delta_total_cash_flow_10yr;
    const deltaIrr5 = zandiProj.delta_irr_5yr;

    let growthOutlook = '';
    if (deltaCF > 0) {
      growthOutlook = `When we overlay Zandi's submarket momentum onto our conservative SSOT baseline, 10-year cumulative cash flow increases by $${deltaCF.toLocaleString()} (from $${baselineCF.toLocaleString()} to $${adjustedCF.toLocaleString()}).`;
    } else if (deltaCF < 0) {
      growthOutlook = `Zandi's momentum adjustment actually reduces 10-year projected cash flow by $${Math.abs(deltaCF).toLocaleString()} versus our baseline — the submarket's growth trajectory is below our conservative assumptions.`;
    } else {
      growthOutlook = `Zandi's submarket momentum closely mirrors our baseline growth assumptions, confirming the underwriting is well-calibrated.`;
    }

    if (deltaIrr5 != null && deltaIrr5 !== 0) {
      const irrBasis = deltaIrr5 > 1 ? deltaIrr5 : deltaIrr5 * 100;
      const direction = irrBasis > 0 ? 'enhances' : 'reduces';
      growthOutlook += ` This ${direction} the 5-year IRR by ${Math.abs(irrBasis).toFixed(1)} percentage points when market momentum is factored in.`;
    }

    if (growthOutlook) paragraphs.push(growthOutlook);
  }

  // --- Paragraph 5: Demographic & Risk Context ---
  const demoProfile = data.investment_intelligence?.demographic_profile;
  const neighborhoodTrend = demoProfile?.neighborhood_trajectory;

  if (demoProfile) {
    let demoNarrative = '';
    const empDiv = demoProfile.employment_diversity;
    const tenantQ = demoProfile.tenant_quality;

    if (neighborhoodTrend === 'improving') {
      demoNarrative = `Demographic signals are favorable: the neighborhood trajectory is improving`;
    } else if (neighborhoodTrend === 'stable') {
      demoNarrative = `Demographics are stable`;
    } else if (neighborhoodTrend === 'declining') {
      demoNarrative = `Caution warranted: neighborhood trajectory is declining`;
    }

    if (empDiv) {
      if (empDiv.score >= 70) {
        demoNarrative += `, supported by diversified local employment (${empDiv.grade})`;
      } else if (empDiv.score < 50) {
        demoNarrative += `, though employment concentration is a risk (${empDiv.grade}) — a downturn in the dominant industry could impact tenant stability`;
      }
    }

    if (tenantQ && tenantQ.score >= 70) {
      demoNarrative += `, with strong tenant quality indicators (${tenantQ.grade})`;
    }

    if (demoNarrative) {
      demoNarrative += '.';
      paragraphs.push(demoNarrative);
    }
  }

  // --- Paragraph 6: Investment Synthesis ---
  const verdict = data.deal_verdict?.verdict;
  const afterTaxIrr5 = data.return_metrics?.irr_5yr_after_tax;
  const bonusDelta = data.bonus_depreciation_comparison?.delta;

  let synthesis = 'Synthesizing all signals: ';
  const bullPoints: string[] = [];
  const bearPoints: string[] = [];

  if (rentGap >= 60) bullPoints.push('large rent gap for value-add upside');
  else if (rentGap >= 40) bullPoints.push('moderate rent uplift opportunity');

  if (vacancy >= 75) bullPoints.push('tight vacancy supporting rent growth');
  if (momentum >= 60) bullPoints.push(`${strengthLabel(momentum)} market momentum`);
  if (permitScore < 40) bullPoints.push('limited new supply competition');

  if (vacancy < 40) bearPoints.push('elevated vacancy risk');
  if (momentum < 40) bearPoints.push('weak growth momentum');
  if (permitScore >= 70) bearPoints.push('heavy incoming supply');
  if (neighborhoodTrend === 'declining') bearPoints.push('declining neighborhood trend');

  if (bullPoints.length > 0) {
    synthesis += `Tailwinds include ${bullPoints.join(', ')}. `;
  }
  if (bearPoints.length > 0) {
    synthesis += `Headwinds to monitor: ${bearPoints.join(', ')}. `;
  }

  if (afterTaxIrr5 != null && bonusDelta?.irr_boost_5yr != null) {
    const boostPct = bonusDelta.irr_boost_5yr > 1 ? bonusDelta.irr_boost_5yr : bonusDelta.irr_boost_5yr * 100;
    const atIrrPct = afterTaxIrr5 > 1 ? afterTaxIrr5 : afterTaxIrr5 * 100;
    synthesis += `With OBBB bonus depreciation, the after-tax 5-year IRR reaches ${atIrrPct.toFixed(1)}% (+${boostPct.toFixed(1)}pp from bonus deduction). `;
  }

  if (verdict === 'GO') {
    synthesis += 'The combined macro and micro data support this investment thesis.';
  } else if (verdict === 'CONDITIONAL') {
    synthesis += 'The data is supportive but does not fully clear all investment thresholds — targeted negotiation on price or terms could bridge the gap.';
  } else if (verdict === 'NO-GO') {
    synthesis += 'Despite favorable market signals, the deal economics do not meet investment criteria at current pricing.';
  }

  paragraphs.push(synthesis);

  return paragraphs;
}

export function MarketDataSynthesis({ data, yardiData }: MarketDataSynthesisProps) {
  const market = useActiveMarket();
  const zandi = data.zandi_scoring;
  if (!zandi) return null;

  const uplift = data.uplift_position;
  const zandiProj = data.zandi_adjusted_projection;
  const paragraphs = buildSynthesis(zandi, uplift, zandiProj, data, yardiData, market.display_name);

  // Key factors for the mini scorecard
  const factors = [
    { name: 'Rent Gap', score: zandi.rent_gap_score, desc: 'Rent growth potential' },
    { name: 'Momentum', score: zandi.momentum_score, desc: 'Market trajectory' },
    { name: 'Vacancy', score: zandi.vacancy_score, desc: 'Rental market tightness' },
    { name: 'Affordability', score: zandi.mobi_score, desc: 'Income-to-rent ratio' },
    { name: 'Valuation', score: zandi.valuation_score, desc: 'Price appreciation' },
    { name: 'Supply', score: zandi.permit_score, desc: 'Development activity' },
    { name: 'Spillover', score: zandi.spillover_score, desc: 'Adjacent submarket effect' },
  ].sort((a, b) => b.score - a.score);

  const topFactor = factors[0];
  const weakFactor = factors[factors.length - 1];

  // Data sources used
  const macroSources = uplift?.macro_sources || ['zandi_scoring'];
  const microSources = uplift?.micro_sources || ['rent_validation', 'return_metrics'];
  const hasYardi = !!yardiData;
  const hasDemographics = !!data.investment_intelligence?.demographic_profile;

  return (
    <div className="rounded-xl border border-purple-500/30 bg-charcoal p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Brain className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <SectionTitle>Market Intelligence Synthesis</SectionTitle>
            <p className="text-sm text-stone-700 mt-0.5">
              What the combined Zandi + local data reveals about this opportunity
            </p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-lg border ${scoreColor(zandi.composite_score)} font-mono text-lg font-bold bg-obsidian border-purple-500/20`}>
          {zandi.composite_score.toFixed(0)}<span className="text-stone-700 text-sm">/100</span>
        </div>
      </div>

      {/* Factor Mini-Scorecard */}
      <div className="grid grid-cols-7 gap-2">
        {factors.map(f => (
          <div key={f.name} className="bg-graphite rounded-lg border border-slate-dark p-2 text-center">
            <p className="text-sm text-stone-700 uppercase tracking-wider truncate">{f.name}</p>
            <p className={`text-sm font-mono font-bold ${scoreColor(f.score)}`}>
              {f.score.toFixed(0)}
            </p>
          </div>
        ))}
      </div>

      {/* Key Insight Bar */}
      <div className="flex gap-3">
        <div className="flex-1 bg-emerald/10 border border-emerald/20 rounded-lg p-3 flex items-start gap-2">
          <Zap className="w-4 h-4 text-emerald flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald">Strongest Signal</p>
            <p className="text-sm text-stone-700">{topFactor.name} ({topFactor.score.toFixed(0)}/100) — {topFactor.desc}</p>
          </div>
        </div>
        <div className="flex-1 bg-amber-400/10 border border-amber-400/20 rounded-lg p-3 flex items-start gap-2">
          <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-400">Watch Factor</p>
            <p className="text-sm text-stone-700">{weakFactor.name} ({weakFactor.score.toFixed(0)}/100) — {weakFactor.desc}</p>
          </div>
        </div>
      </div>

      {/* Synthesis Narrative */}
      <div className="bg-obsidian/50 rounded-lg border border-purple-500/15 p-5 space-y-3">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-sm text-ivory/80 leading-relaxed">
            {p}
          </p>
        ))}
      </div>

      {/* Data Sources Footer */}
      <div className="flex items-center justify-between text-sm text-stone-700 border-t border-slate-dark pt-3">
        <div className="flex flex-wrap gap-2">
          <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 rounded font-medium">Zandi/Moody's</span>
          {hasYardi && <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded font-medium">Yardi Matrix</span>}
          {hasDemographics && <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded font-medium">Demographics</span>}
          <span className="px-2 py-0.5 bg-stone-800 text-stone-700 rounded font-medium">SSOT Engine</span>
        </div>
        <span className="text-stone-700">
          {macroSources.length + microSources.length + (hasYardi ? 1 : 0)} data sources synthesized
        </span>
      </div>
    </div>
  );
}

export default MarketDataSynthesis;
